<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import Button from '../../Button.svelte';
  import Table from '../../Table.svelte';
  import Pagination from '../../Pagination.svelte';
  import Dropdown from '../../Dropdown.svelte';
  import AsyncSearchableDropdown from '../../AsyncSearchableDropdown.svelte';
  import { CharityProfileService, type CharityProfileData, type Charity } from '../../../lib/api/admin/charity-profile.service';
  import { CharityService } from '../../../lib/api/admin/charity.service';
  import Notification from '../../Notification.svelte';
  import BackButton from '../../BackButton.svelte';
  import Breadcrumbs from '../../Breadcrumbs.svelte';
  import { push, location } from 'svelte-spa-router';
  import AddCharityModal from './AddCharityModal.svelte';
  import { formatCharityDgrTaxStatus } from '../../../lib/charity/dgr-tax-status';
  import employersSearchService from '../../../lib/api/admin/employers-search.service';
  import { api } from '../../../lib/api/client';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import ErrorState from '../../ErrorState.svelte';

  export let params: { id: string };

  const returnPathOptions: Record<string, { label: string; href: string }> = {
    '/admin/unconfirmed-bank-details': {
      label: 'Unconfirmed Bank Details',
      href: '/admin/unconfirmed-bank-details'
    },
    '/admin/pending-confirmation': {
      label: 'Pending Confirmation',
      href: '/admin/pending-confirmation'
    }
  };

  function getReturnPath(currentLocation: string): string {
    const queryString = currentLocation.split('?')[1] || '';
    const returnTo = new URLSearchParams(queryString).get('return');
    if (returnTo && returnPathOptions[returnTo]) {
      return returnTo;
    }
    return '/charity-list';
  }

  $: returnPath = getReturnPath($location);
  $: listContext = returnPathOptions[returnPath] ?? {
    label: 'Charities',
    href: '/charity-list'
  };
  
  let loading = false;
  let error: string | null = null;
  let profileData: CharityProfileData | null = null;
  let paymentHistoryPage = 1;
  let itemsPerPage = 20;

  // Payment history filters
  let selectedPaymentStatus = '';
  let selectedPaymentEmployer = '';
  let paymentStatusOptions: Array<{uuid: string, name: string}> = [];

  // Modal state
  let showEditModal = false;
  let charityToEdit: Charity | null = null;
  let showRejectModal = false;
  let rejectionReason = '';
  let actionLoading = false;

  // Notification state
  let notificationShow = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';

  // Logo upload (main profile screen, not edit modal)
  let logoFile: File | null = null;
  let logoPreview = '';
  let logoInput: HTMLInputElement;
  let logoLoading = false;
  let logoDragOver = false;
  let showLogoViewer = false;
  let logoFieldErrors: Record<string, string[]> = {};

  $: currentLogoUrl = logoPreview || profileData?.charity?.logo || '';
  $: charityInitials = (profileData?.charity?.name || 'CH')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase() || 'CH';

  onDestroy(() => {
    revokeLogoPreview();
  });

  function getLogoFieldError(fieldName: string): string | undefined {
    return logoFieldErrors[fieldName]?.[0];
  }

  function revokeLogoPreview() {
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
      logoPreview = '';
    }
  }

  function resetSelectedLogo() {
    logoFile = null;
    revokeLogoPreview();
    if (logoInput) {
      logoInput.value = '';
    }
  }

  function openLogoPicker() {
    logoInput?.click();
  }

  function openLogoViewer() {
    if (!currentLogoUrl) return;
    showLogoViewer = true;
  }

  function closeLogoViewer() {
    showLogoViewer = false;
  }

  function changeLogoFromViewer() {
    closeLogoViewer();
    openLogoPicker();
  }

  function handleLogoViewerKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      closeLogoViewer();
    }
  }

  function handleLogoChange(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    applyLogoFile(input.files?.[0] || null);
  }

  function handleLogoDragOver(event: DragEvent) {
    event.preventDefault();
    logoDragOver = true;
  }

  function handleLogoDragLeave() {
    logoDragOver = false;
  }

  function handleLogoDrop(event: DragEvent) {
    event.preventDefault();
    logoDragOver = false;
    applyLogoFile(event.dataTransfer?.files?.[0] || null);
  }

  function applyLogoFile(file: File | null) {
    delete logoFieldErrors.logo;
    logoFieldErrors = { ...logoFieldErrors };

    if (!file) {
      resetSelectedLogo();
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxBytes = 2 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      logoFieldErrors = { ...logoFieldErrors, logo: ['Please upload a JPG, PNG, or WebP logo.'] };
      resetSelectedLogo();
      return;
    }

    if (file.size > maxBytes) {
      logoFieldErrors = { ...logoFieldErrors, logo: ['Logo must be smaller than 2 MB.'] };
      resetSelectedLogo();
      return;
    }

    revokeLogoPreview();
    logoFile = file;
    logoPreview = URL.createObjectURL(file);
  }

  async function handleLogoSubmit() {
    if (!logoFile || !profileData?.charity?.uuid) {
      showNotification('Choose a logo file before saving.', 'error');
      return;
    }

    logoLoading = true;
    delete logoFieldErrors.logo;
    logoFieldErrors = { ...logoFieldErrors };

    try {
      const result = await CharityService.updateCharityLogo(profileData.charity.uuid, logoFile);
      if (result.success) {
        const updated = Array.isArray(result.data) ? result.data[0] : result.data;
        if (updated?.logo) {
          profileData.charity.logo = updated.logo;
        } else if (logoPreview) {
          profileData.charity.logo = logoPreview;
        }
        profileData = profileData;
        resetSelectedLogo();
        showNotification(result.message || 'Charity logo updated.', 'success');
      } else {
        if (result.error?.response?.data?.errors) {
          logoFieldErrors = { ...logoFieldErrors, ...result.error.response.data.errors };
        }
        showNotification(result.message || 'Could not upload logo. Please try again.', 'error');
      }
    } catch (err: any) {
      showNotification(err?.message || 'Could not upload logo. Please try again.', 'error');
    } finally {
      logoLoading = false;
    }
  }


  // Table columns configuration
  const transactionColumns = [
    {
      key: 'transaction_id',
      label: 'Transaction ID',
      width: '20%'
    },
    {
      key: 'transaction_date',
      label: 'Date',
      width: '15%',
      render: (value: string) => CharityProfileService.formatDate(value)
    },
    {
      key: 'company',
      label: 'Employer',
      width: '25%',
      render: (value: string, row: any) => {
        // Use company field if available (handles multiple employers), otherwise use employer name
        if (value) {
          return value;
        }
        if (row.employer?.name) {
          return row.employer.name;
        }
        return '—';
      }
    },
    {
      key: 'amount',
      label: 'Amount',
      width: '20%',
      render: (value: number) => CharityProfileService.formatCurrency(value)
    },
    {
      key: 'donation_payout_status',
      label: 'Status',
      width: '24%',
      align: 'center',
      render: (value: { name: string }) => `
        <span class="status-badge" style="background-color: ${getStatusBgColor(value.name)}; color: ${getStatusTextColor(value.name)}">
          <svg viewBox="0 0 20 20" fill="currentColor" class="status-icon">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
          ${value.name}
        </span>
      `
    }
  ];

  function getStatusBgColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'completed':
        return '#dcfce7';
      case 'pending':
        return '#fef3c7';
      case 'failed':
        return '#fee2e2';
      default:
        return '#f3f4f6';
    }
  }

  function getStatusTextColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'completed':
        return '#166534';
      case 'pending':
        return '#92400e';
      case 'failed':
        return '#991b1b';
      default:
        return '#374151';
    }
  }

  function showNotification(message: string, type: 'success' | 'error' = 'success') {
    notificationMessage = message;
    notificationType = type;
    notificationShow = true;
    setTimeout(() => notificationShow = false, 4000);
  }

  async function loadPaymentStatusOptions() {
    try {
      const response = await api.get<{success: boolean, data: {donation_payout_statuses: Array<{uuid: string, name: string}>}}>('/resource-lists?types=donation_payout_statuses');
      if (response.data?.success && response.data.data?.donation_payout_statuses) {
        paymentStatusOptions = [
          { uuid: '', name: 'All Statuses' },
          ...response.data.data.donation_payout_statuses
        ];
      } else {
        // Fallback to hardcoded options if API fails
        paymentStatusOptions = [
          { uuid: '', name: 'All Statuses' },
          { uuid: 'pending', name: 'Pending' },
          { uuid: 'completed', name: 'Completed' },
          { uuid: 'failed', name: 'Failed' }
        ];
      }
    } catch (err) {
      console.error('Error loading payment status options:', err);
      paymentStatusOptions = [
        { uuid: '', name: 'All Statuses' },
        { uuid: 'pending', name: 'Pending' },
        { uuid: 'completed', name: 'Completed' },
        { uuid: 'failed', name: 'Failed' }
      ];
    }
  }

  async function searchEmployers(query: string): Promise<Array<{uuid: string, name: string}>> {
    try {
      if (!query.trim()) return [];
      return await employersSearchService.searchEmployers(query, 10);
    } catch (err) {
      console.error('Error searching employers:', err);
      return [];
    }
  }

  async function getEmployerLabel(uuid: string): Promise<string | null> {
    try {
      return await employersSearchService.getEmployerName(uuid);
    } catch (err) {
      console.error('Error getting employer name:', err);
      return null;
    }
  }

  async function loadCharityProfile() {
    if (!params.id) {
      error = 'No charity ID provided';
      return;
    }

    loading = true;
    error = null;
    
    try {
      const response = await CharityProfileService.getCharityProfile(
        params.id, 
        paymentHistoryPage, 
        itemsPerPage,
        selectedPaymentStatus || undefined,
        selectedPaymentEmployer || undefined
      );
      profileData = response.data;
    } catch (err: any) {
      error = err.message || 'Failed to load charity profile';
      showNotification(error ?? 'Failed to load charity profile', 'error');
    } finally {
      loading = false;
    }
  }

  function handleEditDetails() {
    if (!profileData?.charity) return;
    // Make a copy of the charity data to avoid reference issues
    charityToEdit = JSON.parse(JSON.stringify(profileData.charity));
    showEditModal = true;
  }

  function handleCloseModal() {
    showEditModal = false;
    // Clear the charity data after a short delay to avoid UI flicker
    setTimeout(() => {
      charityToEdit = null;
    }, 300);
  }

  async function handleSaveCharity(updatedCharity: any) {
    showNotification('Charity updated successfully', 'success');
    showEditModal = false;
    // Clear the charity data
    charityToEdit = null;
    // Reload the profile data to reflect changes
    await loadCharityProfile();
  }

  function openRejectModal() {
    showRejectModal = true;
  }

  function closeRejectModal() {
    if (!actionLoading) {
      showRejectModal = false;
      rejectionReason = '';
    }
  }

  async function handleConfirmReject() {
    if (!profileData?.charity?.uuid) return;
    actionLoading = true;
    try {
      const result = await CharityProfileService.rejectCharity(
        profileData.charity.uuid,
        rejectionReason.trim() || undefined
      );
      if (result.success) {
        showNotification('Charity rejected.', 'success');
        showRejectModal = false;
        rejectionReason = '';
        await loadCharityProfile();
      } else {
        showNotification(result.message || 'Failed to reject charity', 'error');
      }
    } catch (err: any) {
      showNotification(err?.message || 'Failed to reject charity', 'error');
    } finally {
      actionLoading = false;
    }
  }

  let showDeleteCharityModal = false;
  let deletingCharity = false;
  let bankVerifyLoading = false;
  let showBankVerifyModal = false;
  let bankVerifyIntent: 'verify' | 'clear' | null = null;

  function openBankVerifyModal(intent: 'verify' | 'clear') {
    bankVerifyIntent = intent;
    showBankVerifyModal = true;
  }

  function closeBankVerifyModal() {
    if (bankVerifyLoading) return;
    showBankVerifyModal = false;
    bankVerifyIntent = null;
  }

  async function setBankDetailsVerified(verified: boolean): Promise<boolean> {
    if (!profileData?.charity?.uuid) return false;
    bankVerifyLoading = true;
    try {
      const res = (await CharityService.updateCharity(profileData.charity.uuid, {
        bank_details_verified: verified
      })) as { success?: boolean; message?: string; data?: unknown };
      if (res.success === false) {
        showNotification(res.message || 'Could not update bank verification', 'error');
        return false;
      }
      showNotification(
        verified ? 'Bank details marked as verified.' : 'Bank verification cleared.',
        'success'
      );
      await loadCharityProfile();
      return true;
    } catch (err: any) {
      showNotification(err?.message || 'Could not update bank verification', 'error');
      return false;
    } finally {
      bankVerifyLoading = false;
    }
  }

  async function confirmBankVerifyModal() {
    if (bankVerifyIntent === null) return;
    const ok = await setBankDetailsVerified(bankVerifyIntent === 'verify');
    if (ok) {
      closeBankVerifyModal();
    }
  }

  async function handleDeleteCharity() {
    if (!profileData?.charity?.uuid) return;
    deletingCharity = true;
    try {
      const result = await CharityService.deleteCharity(profileData.charity.uuid);
      if (result.success) {
        showNotification('Charity deleted successfully', 'success');
        push('/charity-list');
      } else {
        showNotification(result.message || 'Failed to delete charity', 'error');
      }
    } catch (err: any) {
      showNotification(err?.message || 'Failed to delete charity', 'error');
    } finally {
      deletingCharity = false;
      showDeleteCharityModal = false;
    }
  }

  function handlePaymentHistoryPageChange(event: CustomEvent<{ page: number }>) {
    paymentHistoryPage = event.detail.page;
    loadCharityProfile();
  }

  function handlePaymentStatusChange(value: string) {
    selectedPaymentStatus = value;
    paymentHistoryPage = 1;
    loadCharityProfile();
  }

  function handlePaymentEmployerChange(value: string) {
    selectedPaymentEmployer = value;
    paymentHistoryPage = 1;
    loadCharityProfile();
  }

  function handleResetPaymentFilters() {
    selectedPaymentStatus = '';
    selectedPaymentEmployer = '';
    paymentHistoryPage = 1;
    loadCharityProfile();
  }

  function formatCurrency(value: number): string {
    return CharityProfileService.formatCurrency(value);
  }

  onMount(async () => {
    await loadPaymentStatusOptions();
    loadCharityProfile();
  });
</script>

<div class="charity-profile">
  <Breadcrumbs items={[
    { label: 'Admin', href: '/admin-dashboard' },
    { label: listContext.label, href: listContext.href },
    { label: profileData?.charity?.name || 'Charity Profile' }
  ]} />
  <BackButton text="Back" onClick={() => push(returnPath)} />       
  {#if loading}
    <div style="min-height: 400px; padding: 2rem;">
      <LoadingSkeleton lines={8} height="2rem" />
    </div>
  {:else if error}
    <ErrorState 
      message="Failed to load charity profile" 
      description={error}
      onRetry={loadCharityProfile}
    />
  {:else if profileData}
    <div class="profile-content" class:is-modal-open={showEditModal || showRejectModal || showDeleteCharityModal || showBankVerifyModal}>
    <!-- Header Section with Payout Totals -->
    <div class="header-section">
      <div class="header-content">
        <h1>{profileData.charity.name}</h1>
          {#if profileData.charity.rejected}
          <div class="verified-badge-header rejected">
            <svg viewBox="0 0 20 20" fill="currentColor" class="verified-icon">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
            </svg>
            <span>Rejected</span>
          </div>
        {:else if profileData.charity.verified === 1}
          <div class="verified-badge-header verified">
              <svg viewBox="0 0 20 20" fill="currentColor" class="verified-icon">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <span>Verified</span>
            </div>
        {:else}
          <div class="verified-badge-header unverified">
            <svg viewBox="0 0 20 20" fill="currentColor" class="verified-icon">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
            </svg>
            <span>Not Verified</span>
          </div>
          {/if}
        </div>
      <div class="payout-metrics">
        <div class="payout-metric">
          <div class="payout-label">Total Payout Value</div>
          <div class="payout-value">{formatCurrency(profileData.payout_totals?.total_payout_value || 0)}</div>
        </div>
        <div class="payout-metric">
          <div class="payout-label">Pending Payout Amount</div>
          <div class="payout-value pending">{formatCurrency(profileData.payout_totals?.pending_payout_amount || 0)}</div>
        </div>
      </div>
    </div>

    <!-- Charity Details Card -->
    <div class="details-card organisation-card">
      <div class="card-top">
        <div class="charity-logo-section">
          <form class="admin-logo-form" on:submit|preventDefault={handleLogoSubmit}>
            <div
              class="admin-logo-wrap"
              role="region"
              aria-label="Logo upload drop zone"
              on:dragover={handleLogoDragOver}
              on:dragleave={handleLogoDragLeave}
              on:drop={handleLogoDrop}
            >
              {#if currentLogoUrl}
                <button
                  type="button"
                  class="admin-logo-thumb has-logo"
                  class:drag-over={logoDragOver}
                  on:click={openLogoViewer}
                  aria-label="View charity logo full size"
                >
                  <img src={currentLogoUrl} alt="{profileData.charity.name} logo" />
                  <span class="admin-logo-overlay">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    View
                  </span>
                </button>
                <button
                  type="button"
                  class="admin-logo-change"
                  on:click={openLogoPicker}
                  disabled={logoLoading}
                >
                  Change logo
                </button>
              {:else}
                <button
                  type="button"
                  class="admin-logo-thumb"
                  class:drag-over={logoDragOver}
                  on:click={openLogoPicker}
                  aria-label="Upload charity logo"
                >
                  <span class="admin-logo-initials" aria-hidden="true">{charityInitials}</span>
                  <span class="admin-logo-overlay">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Upload
                  </span>
                </button>
              {/if}
            </div>

            <div class="card-title-block">
              <span class="card-eyebrow">Organisation</span>
              <h2>Charity details</h2>
              <p class="card-subtitle">Public profile and contact information donors and employers see.</p>
              {#if logoFile}
                <div class="admin-logo-actions">
                  <span class="logo-selected">Selected: {logoFile.name}</span>
                  <button type="button" class="logo-clear-button" on:click={resetSelectedLogo} disabled={logoLoading}>
                    Remove
                  </button>
                  <Button type="submit" variant="primary" disabled={logoLoading}>
                    {logoLoading ? 'Saving...' : 'Save logo'}
                  </Button>
                </div>
              {:else}
                <p class="logo-help">
                  {#if currentLogoUrl}
                    Click the logo to view full size, or use Change logo to upload. JPG, PNG, or WebP up to 2 MB.
                  {:else}
                    JPG, PNG, or WebP up to 2 MB. Drag and drop or click the logo area.
                  {/if}
                </p>
              {/if}
              {#if getLogoFieldError('logo')}
                <div class="field-error">{getLogoFieldError('logo')}</div>
              {/if}
            </div>

            <input
              bind:this={logoInput}
              id="admin-charity-profile-logo"
              class="logo-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              on:change={handleLogoChange}
              disabled={logoLoading}
            />
          </form>
        </div>
        <div class="details-actions">
          <Button
            variant="primary"
            on:click={handleEditDetails}
          >
            Edit organisation
          </Button>
          {#if profileData.charity.verified !== 1 && !profileData.charity.rejected}
            <Button
              variant="secondary"
              on:click={openRejectModal}
              class="reject-button"
            >
              Reject charity
            </Button>
          {/if}
          <Button variant="danger" on:click={() => showDeleteCharityModal = true}>
            Delete
          </Button>
        </div>
      </div>

        <div class="charity-info">
          <div class="info-group">
            <span class="info-label">Charity Name</span>
            <div class="info-value">{profileData.charity.name}</div>
          </div>
        <div class="info-group">
          <span class="info-label">ABN</span>
          <div class="info-value">{profileData.charity.abn || profileData.charity.charity_government_id || '—'}</div>
        </div>
        <div class="info-group">
          <span class="info-label">Category</span>
          <div class="info-value">{profileData.charity.category?.name ?? '—'}</div>
        </div>
        <div class="info-group">
          <span class="info-label">Charity Tax Status</span>
          <div class="info-value">{formatCharityDgrTaxStatus(profileData.charity.dgr_tax_status)}</div>
        </div>
          <div class="info-group">
            <span class="info-label">Contact Name</span>
          <div class="info-value">{profileData.charity.contact_name || '—'}</div>
        </div>
          <div class="info-group">
            <span class="info-label">Contact Email</span>
          <div class="info-value">
            {#if profileData.charity.contact_email}
              <a href="mailto:{profileData.charity.contact_email}" class="info-link">{profileData.charity.contact_email}</a>
            {:else}
              —
            {/if}
          </div>
          </div>
          <div class="info-group">
            <span class="info-label">Contact Phone</span>
          <div class="info-value">{profileData.charity.contact_phone || '—'}</div>
        </div>
        <div class="info-group">
          <span class="info-label">Website</span>
          <div class="info-value">
            {#if profileData.charity.website}
              <a href={profileData.charity.website.startsWith('http') ? profileData.charity.website : `https://${profileData.charity.website}`} target="_blank" rel="noopener noreferrer" class="info-link">
                {profileData.charity.website}
                <svg viewBox="0 0 20 20" fill="currentColor" class="external-link-icon">
                  <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
                  <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/>
                </svg>
              </a>
            {:else}
              —
            {/if}
          </div>
        </div>
        <div class="info-group">
          <span class="info-label">Head Office Location</span>
          <div class="info-value">{profileData.charity.head_office_location || '—'}</div>
        </div>
        {#if profileData.charity.description}
          <div class="info-group info-group-full">
            <span class="info-label">Description</span>
            <div class="info-value info-value-multiline">{profileData.charity.description}</div>
          </div>
        {:else}
          <div class="info-group">
            <span class="info-label">Description</span>
            <div class="info-value">—</div>
          </div>
        {/if}
        {#if profileData.charity.rejected && profileData.charity.rejection_reason}
          <div class="info-group rejection-reason-block">
            <span class="info-label">Reason for rejection</span>
            <div class="info-value rejection-reason-text">{profileData.charity.rejection_reason}</div>
          </div>
        {/if}
      </div>
    </div>

    <div class="details-card bank-card">
      <div class="card-top bank-card-top">
        <div class="card-title-block">
          <span class="card-eyebrow">Payouts</span>
          <h2>Bank account</h2>
          <p class="card-subtitle">Account details for donations and phone verification before payouts.</p>
        </div>
        <div class="bank-admin-status-row bank-admin-status-row-header">
          <span class="bank-admin-status-label">Phone verification</span>
          <span
            class="bank-admin-pill"
            class:bank-admin-pill-verified={profileData.charity.bank_details_status === 'verified'}
            class:bank-admin-pill-unverified={profileData.charity.bank_details_status !== 'verified'}
          >
            {profileData.charity.bank_details_status === 'verified' ? 'Verified' : 'Pending'}
          </span>
        </div>
      </div>
      <div class="bank-details-section">
        {#if profileData.charity.bank_details_verified_at}
          <p class="bank-verified-meta">
            Marked verified {CharityProfileService.formatDate(profileData.charity.bank_details_verified_at)}
          </p>
        {/if}
        <div class="bank-verify-control">
          {#if profileData.charity.bank_details_status !== 'verified'}
            <div class="bank-verify-actions">
              <Button
                variant="primary"
                type="button"
                disabled={bankVerifyLoading}
                on:click={() => openBankVerifyModal('verify')}
              >
                Confirm phone verification
              </Button>
            </div>
            <p class="bank-verify-hint">
              Use after you have phoned the charity and confirmed BSB, account number, and account name.
            </p>
          {:else}
            <div class="bank-verify-actions">
              <Button
                variant="secondary"
                type="button"
                disabled={bankVerifyLoading}
                on:click={() => openBankVerifyModal('clear')}
              >
                Clear phone verification
              </Button>
            </div>
            <p class="bank-verify-hint bank-verify-hint-secondary">
              Clears the verified status if you need to reconfirm after a bank detail change.
            </p>
          {/if}
        </div>
        <div class="charity-info">
          <div class="info-group">
            <span class="info-label">BSB Number</span>
            <div class="info-value">{profileData.charity.verified_bsb_no || '—'}</div>
          </div>
          <div class="info-group">
            <span class="info-label">Account Number</span>
            <div class="info-value">{profileData.charity.verified_acc_no || '—'}</div>
          </div>
          <div class="info-group">
            <span class="info-label">Account Name</span>
            <div class="info-value">{profileData.charity.verified_acc_name || '—'}</div>
          </div>
          </div>
        </div>
      </div>

    {#if showBankVerifyModal && bankVerifyIntent}
      <div
        class="modal-overlay"
        role="presentation"
        on:click|self={closeBankVerifyModal}
        on:keydown={(e) => e.key === 'Escape' && closeBankVerifyModal()}
      >
        <div class="modal-content confirm-modal" role="dialog" aria-modal="true" aria-labelledby="bank-verify-modal-title">
          <h3 id="bank-verify-modal-title">
            {bankVerifyIntent === 'verify' ? 'Mark bank details as verified?' : 'Clear phone verification?'}
          </h3>
          {#if bankVerifyIntent === 'verify'}
            <p>
              Confirm that you have phoned this charity and checked that the BSB, account number, and account name
              match what they will use for payouts. The charity will see a <strong>Verified</strong> status on their profile.
            </p>
          {:else}
            <p>
              This removes the verified status. The charity will see <strong>Unverified</strong> until you confirm
              by phone again (for example after they change bank details).
            </p>
          {/if}
          <div class="modal-actions">
            <Button variant="secondary" type="button" on:click={closeBankVerifyModal} disabled={bankVerifyLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              on:click={confirmBankVerifyModal}
              disabled={bankVerifyLoading}
              class={bankVerifyIntent === 'clear' ? 'reject-confirm-button' : ''}
            >
              {bankVerifyLoading
                ? 'Saving…'
                : bankVerifyIntent === 'verify'
                  ? 'Yes, mark as verified'
                  : 'Yes, clear verification'}
            </Button>
          </div>
        </div>
      </div>
    {/if}

    <!-- Reject confirmation modal -->
    {#if showRejectModal}
      <div class="modal-overlay" role="dialog" aria-labelledby="reject-charity-modal-title" aria-modal="true">
        <div class="modal-content confirm-modal">
          <h3 id="reject-charity-modal-title">Reject charity?</h3>
          <p>This charity will be marked as rejected and will no longer be able to use the system.</p>
          <div class="reject-reason-field">
            <label for="rejection-reason-charity">Reason for rejection (optional)</label>
            <textarea
              id="rejection-reason-charity"
              bind:value={rejectionReason}
              placeholder="e.g. Charity declined to participate, duplicate entry…"
              rows="3"
              maxlength="1000"
              disabled={actionLoading}
              aria-describedby="rejection-reason-charity-hint"
            />
            <span id="rejection-reason-charity-hint" class="field-hint">If provided, this reason will be stored and sent to the user who nominated this charity.</span>
          </div>
          <div class="modal-actions">
            <Button variant="secondary" on:click={closeRejectModal} disabled={actionLoading}>Cancel</Button>
            <Button variant="primary" on:click={handleConfirmReject} disabled={actionLoading} class="reject-confirm-button">
              {actionLoading ? 'Rejecting…' : 'Reject'}
            </Button>
          </div>
        </div>
      </div>
    {/if}

    {#if showDeleteCharityModal}
      <div class="modal-overlay" role="presentation" on:click|self={() => showDeleteCharityModal = false} on:keydown={(e) => e.key === 'Escape' && (showDeleteCharityModal = false)}>
        <div class="modal-content confirm-modal" role="dialog" aria-modal="true" aria-labelledby="delete-charity-modal-title">
          <h3 id="delete-charity-modal-title">Delete Charity</h3>
          <p>Are you sure you want to delete <strong>{profileData?.charity?.name || 'this charity'}</strong>? This action cannot be undone and will remove all associated data.</p>
          <div class="modal-actions">
            <Button variant="secondary" on:click={() => showDeleteCharityModal = false} disabled={deletingCharity}>Cancel</Button>
            <Button variant="danger" on:click={handleDeleteCharity} disabled={deletingCharity}>
              {deletingCharity ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    {/if}

    <!-- Payment History Section -->
    <div class="payment-history-section">
      <div class="section-header">
        <div class="header-left">
          <h2>Payment History</h2>
          {#if profileData.transaction_history?.total > 0}
            <p class="section-subtitle">
              Showing {((paymentHistoryPage - 1) * itemsPerPage) + 1}-{Math.min(paymentHistoryPage * itemsPerPage, profileData.transaction_history.total)} of {profileData.transaction_history.total} payments
            </p>
          {/if}
        </div>
      </div>

      <!-- Payment History Filters -->
      <div class="filters-bar">
        <div class="filters-group">
          <div class="filter-item">
            <label for="payment-status-filter" class="filter-label">Status</label>
            <Dropdown
              id="payment-status-filter"
              options={paymentStatusOptions}
              selectedValue={selectedPaymentStatus}
              onSelectionChange={handlePaymentStatusChange}
              placeholder="All Statuses"
            />
          </div>
          <div class="filter-item">
            <label for="payment-employer-filter" class="filter-label">Employer</label>
            <AsyncSearchableDropdown
              id="payment-employer-filter"
              searchFunction={searchEmployers}
              getSelectedLabel={getEmployerLabel}
              selectedValue={selectedPaymentEmployer}
              onSelectionChange={handlePaymentEmployerChange}
              placeholder="All Employers"
            />
          </div>
          {#if selectedPaymentStatus || selectedPaymentEmployer}
            <div class="filter-item">
              <Button variant="secondary" on:click={handleResetPaymentFilters} class="reset-btn">
                Reset Filters
              </Button>
            </div>
          {/if}
        </div>
      </div>

      {#if loading}
        <div class="loading-container">
          <div class="loading-spinner"></div>
          <p>Loading payment history...</p>
        </div>
      {:else if profileData.transaction_history?.data?.length === 0}
        <div class="empty-state">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
          <h3>No payment history</h3>
          <p>No payment records found for this charity.</p>
    </div>
      {:else}
        <div class="table-container">
      <Table
        columns={transactionColumns}
        data={profileData.transaction_history?.data || []}
            loading={false}
      />
        </div>
      
        {#if profileData.transaction_history && profileData.transaction_history.last_page > 1}
        <div class="pagination-container">
            <Pagination
              currentPage={paymentHistoryPage}
              totalPages={profileData.transaction_history.last_page}
              on:pageChange={handlePaymentHistoryPageChange}
            />
          </div>
        {/if}
      {/if}
    </div>
    </div>
  {/if}
</div>

<!-- Logo viewer modal -->
{#if showLogoViewer && currentLogoUrl && profileData}
  <div
    class="modal-overlay"
    role="presentation"
    on:click|self={closeLogoViewer}
    on:keydown={(e) => e.key === 'Escape' && closeLogoViewer()}
  >
    <div
      class="modal-content logo-viewer-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-logo-viewer-title"
    >
      <div class="logo-viewer-header">
        <h3 id="admin-logo-viewer-title">{profileData.charity.name} logo</h3>
        <button type="button" class="modal-close" on:click={closeLogoViewer} aria-label="Close">×</button>
      </div>

      <div class="logo-viewer-body">
        <img src={currentLogoUrl} alt="{profileData.charity.name} logo full size" />
      </div>

      <div class="logo-viewer-footer">
        <Button variant="secondary" on:click={closeLogoViewer} type="button">Close</Button>
        <Button variant="primary" on:click={changeLogoFromViewer} type="button">Change logo</Button>
      </div>
    </div>
  </div>
{/if}

<!-- Add Charity Modal for editing -->
{#if profileData}
  <AddCharityModal 
    open={showEditModal}
    onClose={handleCloseModal}
    onSave={handleSaveCharity}
    charity={charityToEdit}
  />
{/if}

<Notification 
  bind:show={notificationShow}
  type={notificationType}
  message={notificationMessage}
/>

<style>
  .charity-profile {
    padding: 1.5rem 2rem;
    width: 100%;
    background: #f5f3ef;
  }

  .profile-content.is-modal-open {
    pointer-events: none;
    user-select: none;
  }

  .profile-content.is-modal-open .modal-overlay {
    pointer-events: auto;
    user-select: auto;
  }

  .profile-content.is-modal-open > :not(.modal-overlay) {
    opacity: 0.55;
    filter: grayscale(0.15);
    transition: opacity 0.2s ease;
  }

  .header-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem 1.75rem;
    margin-bottom: 1.25rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .header-content h1 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .verified-badge-header {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .verified-badge-header.verified {
    background: #dcfce7;
    color: #166534;
  }

  .verified-badge-header.unverified {
    background: #fef3c7;
    color: #92400e;
  }

  .verified-badge-header.rejected {
    background: #fee2e2;
    color: #991b1b;
  }

  .payout-metrics {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  .payout-metric {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .payout-label {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
  }

  .payout-value {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
  }

  .payout-value.pending {
    color: #f59e0b;
  }

  .loading-container,
  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #f0b429;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .details-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem 1.75rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    margin-bottom: 1.25rem;
  }

  .card-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
    padding-bottom: 1.25rem;
    border-bottom: 1px solid #f3f4f6;
  }

  .bank-card-top {
    align-items: center;
    flex-wrap: wrap;
  }

  .card-title-block h2 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0.25rem 0 0;
  }

  .card-eyebrow {
    display: block;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #9ca3af;
  }

  .card-subtitle {
    margin: 0.35rem 0 0;
    font-size: 0.8125rem;
    line-height: 1.45;
    color: #6b7280;
    max-width: 36rem;
  }

  .details-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
    flex-shrink: 0;
  }

  :global(.reject-button) {
    border-color: #dc2626 !important;
    color: #dc2626 !important;
  }

  :global(.reject-button:hover) {
    background-color: #fef2f2 !important;
  }

  .charity-logo-section {
    flex: 1;
    min-width: 0;
  }

  .admin-logo-form {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 1rem;
    margin: 0;
  }

  .admin-logo-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  .admin-logo-thumb {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 5.5rem;
    height: 5.5rem;
    padding: 0;
    border: 2px dashed #d1d5db;
    border-radius: 14px;
    background: white;
    cursor: pointer;
    overflow: hidden;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
  }

  .admin-logo-thumb:hover,
  .admin-logo-thumb.drag-over {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 4px rgba(255, 180, 0, 0.15);
    transform: translateY(-1px);
  }

  .admin-logo-thumb:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 4px rgba(255, 180, 0, 0.2);
  }

  .admin-logo-thumb.has-logo {
    border-style: solid;
    padding: 0.35rem;
  }

  .admin-logo-thumb img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 10px;
  }

  .admin-logo-initials {
    font-size: 1.25rem;
    font-weight: 700;
    color: #9ca3af;
    letter-spacing: 0.04em;
  }

  .admin-logo-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.2rem;
    background: rgba(17, 24, 39, 0.55);
    color: white;
    font-size: 0.6875rem;
    font-weight: 600;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .admin-logo-overlay svg {
    width: 1.1rem;
    height: 1.1rem;
  }

  .admin-logo-thumb:hover .admin-logo-overlay,
  .admin-logo-thumb.drag-over .admin-logo-overlay {
    opacity: 1;
  }

  .admin-logo-change {
    padding: 0;
    border: none;
    background: none;
    color: #1d4ed8;
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .admin-logo-change:hover:not(:disabled) {
    color: #1e40af;
  }

  .admin-logo-change:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .admin-logo-change:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.2);
    border-radius: 4px;
  }

  .admin-logo-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  .logo-help {
    margin: 0.5rem 0 0;
    color: #6b7280;
    font-size: 0.8125rem;
    line-height: 1.5;
  }

  .logo-selected {
    font-size: 0.8125rem;
    color: #374151;
  }

  .logo-clear-button {
    padding: 0.375rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: var(--border-radius-button);
    background: white;
    color: #374151;
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .logo-clear-button:hover:not(:disabled) {
    background: #f3f4f6;
  }

  .logo-clear-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .logo-file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .field-error {
    margin-top: 0.375rem;
    font-size: 0.75rem;
    color: #ef4444;
  }

  .logo-viewer-modal {
    background: white;
    border-radius: 10px;
    max-width: min(720px, 92vw);
    width: 90%;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    overflow: hidden;
  }

  .logo-viewer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .logo-viewer-header h3 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
  }

  .modal-close {
    border: none;
    background: none;
    font-size: 1.5rem;
    line-height: 1;
    color: #6b7280;
    cursor: pointer;
    padding: 0.25rem;
  }

  .modal-close:hover {
    color: #111827;
  }

  .logo-viewer-body {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem 2rem;
    background: #f9fafb;
    max-height: min(70vh, 640px);
    overflow: auto;
  }

  .logo-viewer-body img {
    max-width: 100%;
    max-height: min(65vh, 600px);
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(17, 24, 39, 0.12);
    background: white;
  }

  .logo-viewer-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid #e5e7eb;
  }

  .bank-details-section {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }

  .bank-admin-status-row-header {
    margin-bottom: 0;
  }

  .bank-verify-control {
    margin-bottom: 1.25rem;
    padding: 1rem 1.125rem;
    background: #faf8f4;
    border: 1px solid #f3f4f6;
    border-radius: 10px;
  }

  .verified-icon {
    width: 16px;
    height: 16px;
  }

  .charity-info {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
    margin-bottom: 0;
  }

  .bank-card .charity-info {
    margin-bottom: 0;
  }

  .bank-admin-status-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
  }

  .bank-admin-status-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  .bank-admin-pill {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
  }

  .bank-admin-pill-verified {
    background: #d1fae5;
    color: #065f46;
  }

  .bank-admin-pill-unverified {
    background: #fef3c7;
    color: #92400e;
  }

  .bank-verified-meta {
    font-size: 0.8125rem;
    color: #6b7280;
    margin: 0 0 1rem 0;
  }

  .bank-verify-control {
    margin-bottom: 1.25rem;
  }

  .bank-verify-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .bank-verify-hint {
    font-size: 0.8125rem;
    color: #6b7280;
    line-height: 1.45;
    margin: 0.5rem 0 0 0;
    max-width: 32rem;
  }

  .bank-verify-hint-secondary {
    margin-top: 0.375rem;
  }

  .info-link {
    color: var(--color-primary, #f59e0b);
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    transition: color 0.2s;
  }

  .info-link:hover {
    color: var(--color-primary-hover, #f0a800);
    text-decoration: underline;
  }

  .external-link-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  .info-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .info-group .info-label {
    font-size: 0.875rem;
    color: #6b7280;
  }

  .info-value {
    font-size: 1rem;
    color: #111827;
    padding: 0.75rem;
    background: #f9fafb;
    border-radius: 6px;
    word-break: break-word;
  }

  .info-group-full {
    grid-column: 1 / -1;
  }

  .info-value-multiline {
    white-space: pre-wrap;
  }

  .rejection-reason-block {
    margin-top: 0.5rem;
  }

  .rejection-reason-text {
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    color: #991b1b;
    white-space: pre-wrap;
  }

  .modal-overlay {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
  }

  .modal-content.confirm-modal {
    background: white;
    border-radius: 10px;
    padding: 1.5rem;
    max-width: 420px;
    width: 90%;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  }

  .modal-content.confirm-modal h3 {
    margin: 0 0 0.75rem 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
  }

  .modal-content.confirm-modal p {
    margin: 0 0 1rem 0;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .reject-reason-field {
    margin-bottom: 1.25rem;
  }

  .reject-reason-field label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.375rem;
  }

  .reject-reason-field textarea {
    width: 100%;
    min-height: 80px;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    font-family: inherit;
    resize: vertical;
    box-sizing: border-box;
  }

  .reject-reason-field textarea:focus {
    outline: none;
    border-color: var(--color-primary, #f59e0b);
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
  }

  .reject-reason-field textarea:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }

  .field-hint {
    display: block;
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.375rem;
  }

  .modal-actions {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
  }

  :global(.reject-confirm-button) {
    background-color: #dc2626 !important;
    color: white !important;
    border-color: #dc2626 !important;
  }

  :global(.reject-confirm-button:hover:not(:disabled)) {
    background-color: #b91c1c !important;
  }

  .payment-history-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    margin-bottom: 2rem;
  }

  .section-header {
    margin-bottom: 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .header-left {
    flex: 1;
  }

  .section-header h2 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
  }

  .section-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
  }

  .filters-bar {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }

  .filters-group {
    display: flex;
    gap: 1rem;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  .filter-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    min-width: 200px;
  }

  .filter-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  .table-container {
    overflow-x: auto;
  }

  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
    color: #6b7280;
  }

  .empty-state svg {
    color: #9ca3af;
    margin-bottom: 1rem;
  }

  .empty-state h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.5rem 0;
  }

  .empty-state p {
    font-size: 0.875rem;
    margin: 0;
  }

  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
  }

  :global(.status-icon) {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  /* Medium screens */
  @media (max-width: 992px) {
    .charity-info {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  /* Small screens */
  @media (max-width: 768px) {
    .charity-profile {
      padding: 1rem;
    }

    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }

    .payout-metrics {
      grid-template-columns: 1fr;
      width: 100%;
    }

    .card-top {
      flex-direction: column;
      align-items: stretch;
    }

    .details-actions {
      justify-content: flex-start;
    }

    .charity-info {
      grid-template-columns: 1fr;
    }
  }

  /* Extra small screens */
  @media (max-width: 480px) {
    .charity-profile {
      padding: 0.75rem;
    }

    .loading-container,
    .details-card, 
    .card-title-block h2 {
      font-size: 1.125rem;
    }

    .info-group .info-label {
      font-size: 0.75rem;
    }

    .info-value {
      font-size: 0.875rem;
      padding: 0.5rem;
    }

  }
</style>